import { InviteCodeSource, InviteCodeStatus, WaitlistStatus } from './types';

export type Badge = { label: string; classes: string };

const NEUTRAL_BADGE = 'bg-gray-100 text-gray-700';

export const STATUS_LABEL: Record<InviteCodeStatus, string> = {
  active: 'Active',
  exhausted: 'Exhausted',
  expired: 'Expired',
  revoked: 'Revoked',
};

export const STATUS_CLASSES: Record<InviteCodeStatus, string> = {
  active: 'bg-green-100 text-green-800',
  exhausted: NEUTRAL_BADGE,
  expired: 'bg-yellow-100 text-yellow-800',
  revoked: 'bg-red-100 text-red-800',
};

const WAITLIST_STATUS_CLASSES: Record<WaitlistStatus, string> = {
  pending: NEUTRAL_BADGE,
  invited: 'bg-green-100 text-green-800',
  converted: 'bg-blue-100 text-blue-800',
};

const WAITLIST_STATUS_LABEL: Record<WaitlistStatus, string> = {
  pending: 'Pending',
  invited: 'Invited',
  converted: 'Converted',
};

// These statuses are the server's to define, so the client can be handed one it
// has never heard of. A `Record` lookup returns undefined there and reading
// `.classes` off it throws inside render, which unmounts the whole table — one
// unrecognised row must degrade to a plain badge, not a blank tab.
const badgeFor = <T extends string>(
  labels: Record<T, string>,
  classes: Record<T, string>,
  status: T
): Badge => ({
  label: labels[status] ?? status,
  classes: classes[status] ?? NEUTRAL_BADGE,
});

export const codeStatusBadge = (status: InviteCodeStatus): Badge =>
  badgeFor(STATUS_LABEL, STATUS_CLASSES, status);

export const waitlistStatusBadge = (status: WaitlistStatus): Badge =>
  badgeFor(WAITLIST_STATUS_LABEL, WAITLIST_STATUS_CLASSES, status);

// The wire values are lower-case enum names; these are how they're shown. The
// key set doubles as the option list in the generate-codes modal, so the two
// can't drift from each other or from the union in types.ts.
export const SOURCE_LABEL: Record<InviteCodeSource, string> = {
  admin: 'Admin',
  referral: 'Referral',
  waitlist: 'Waitlist',
  partner: 'Partner',
};

export const SOURCES = Object.keys(SOURCE_LABEL) as InviteCodeSource[];

export const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// A `<input type="date">` yields a bare "YYYY-MM-DD". `new Date("YYYY-MM-DD")`
// parses it as UTC midnight, which in a negative-offset zone (e.g. PR, UTC-4)
// lands on the *previous* evening — so codes would expire ~a day early. Anchor
// the picked day to local end-of-day so it stays valid through the whole day.
export const toExpiryIso = (day: string | null): string | null => {
  if (!day) return null;
  return new Date(`${day}T23:59:59`).toISOString();
};

export const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
